import { pickT } from "@repo/shared";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { VegaEmbed } from "react-vega";
import { useContainerWidth } from "@/lib/hooks/use-container-width";
import { useUserLanguage } from "@/lib/i18n/context";
import type { TimeSeriesData } from "@/lib/utils/vega-chart-shared";
import { T } from "./line-chart.i18n";
import { createLineChartSpec } from "./vega-specs";

type LineChartProps = {
  data: TimeSeriesData[];
  width?: number | "container";
  height?: number;
  title?: string;
  yAxisLabel?: string;
  primaryColor?: string;
  lang?: string;
  /** Zero-based y-axis with this many extra gridlines of headroom above the data max. */
  extraYGridlines?: number;
  /** Rate behind the bank comparison series — shown in the legend label. */
  bankReturnPct?: number;
};

/**
 * Client-side line chart component using Vega-Lite
 *
 * Uses the same chart specification as server-side rendering,
 * ensuring consistent appearance in UI and PDFs.
 */
export function LineChart({
  data,
  width = "container",
  height = 400,
  title,
  yAxisLabel,
  primaryColor,
  lang,
  extraYGridlines,
  bankReturnPct,
}: LineChartProps) {
  const { userLanguage } = useUserLanguage();
  // One language for the labels and the Vega tooltips.
  const chartLang = lang ?? userLanguage;
  const t = pickT(T, chartLang);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  // When width is "container", measure the wrapper and render at that pixel
  // width so the chart fills its column / full-width slot (see useContainerWidth).
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();
  const effectiveWidth = typeof width === "number" ? width : containerWidth;

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === "dark";

  // Generate spec on client side and ensure it's serializable
  const spec = useMemo(() => {
    if (data.length === 0 || effectiveWidth === null) {
      return null;
    }

    const chartSpec = createLineChartSpec(data, {
      width: effectiveWidth,
      height,
      title,
      yAxisLabel,
      isDark,
      primaryColor,
      lang: chartLang,
      extraYGridlines,
      bankReturnPct,
    });
    // Ensure the spec is JSON-serializable by parsing/stringifying
    return JSON.parse(JSON.stringify(chartSpec));
  }, [
    data,
    effectiveWidth,
    height,
    title,
    yAxisLabel,
    isDark,
    primaryColor,
    chartLang,
    extraYGridlines,
    bankReturnPct,
  ]);

  if (data.length === 0) {
    return (
      <div className="w-full p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noData}</p>
      </div>
    );
  }

  // Keep the measured wrapper mounted (with a height reservation) while the
  // container width is being measured, to avoid layout shift on first paint.
  return (
    <div
      ref={containerRef}
      className="w-full [&_svg]:max-w-full [&_svg]:h-auto"
      style={{ minHeight: height }}
    >
      {spec && (
        <VegaEmbed
          spec={spec}
          options={{
            actions: false,
            renderer: "svg",
          }}
        />
      )}
    </div>
  );
}
